import React from 'react'
import { Box, Typography } from '@mui/material'
import { IconAlertCircle, IconCheck, IconSparkles, IconTrendingUp } from '@tabler/icons-react'

export interface MenuSummary {
  quickTake?: string
  goodFor?: string
  watchOut?: string
  friendPick?: string
}

interface SavedMenuSummaryCardProps {
  summary?: MenuSummary
}

const SavedMenuSummaryCard: React.FC<SavedMenuSummaryCardProps> = ({
  summary,
}): JSX.Element | null => {
  // Hide if no summary data
  if (
    !summary ||
    (!summary.quickTake &&
      !summary.goodFor &&
      !summary.watchOut &&
      !summary.friendPick)
  ) {
    return null
  }

  const sections = [
    {
      key: 'quickTake',
      label: 'Quick take',
      content: summary.quickTake,
      icon: IconSparkles,
      accentColor: '#6366F1',
      bgColor: 'rgba(99, 102, 241, 0.05)',
    },
    {
      key: 'goodFor',
      label: 'Good for',
      content: summary.goodFor,
      icon: IconCheck,
      accentColor: '#10B981',
      bgColor: 'rgba(16, 185, 129, 0.05)',
    },
    {
      key: 'watchOut',
      label: 'Watch out',
      content: summary.watchOut,
      icon: IconAlertCircle,
      accentColor: '#F59E0B',
      bgColor: 'rgba(245, 158, 11, 0.05)',
    },
    {
      key: 'friendPick',
      label: 'Friend pick',
      content: summary.friendPick,
      icon: IconTrendingUp,
      accentColor: '#EC4899',
      bgColor: 'rgba(236, 72, 153, 0.05)',
    },
  ].filter(s => s.content) // Only show sections with content

  if (sections.length === 0) return null

  return (
    <Box sx={{ mb: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          component="p"
          sx={{
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '0.5px',
            color: '#6B7580',
            textTransform: 'uppercase',
            mb: 0.5,
          }}
        >
          AI Menu Insights
        </Typography>
      </Box>

      {/* Sections Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: { xs: '12px', sm: '16px' },
        }}
      >
        {sections.map((section) => {
          const IconComponent = section.icon
          return (
            <Box
              key={section.key}
              sx={{
                padding: '16px',
                borderRadius: '12px',
                backgroundColor: section.bgColor,
                border: '1px solid',
                borderColor: 'transparent',
                transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: section.accentColor,
                  boxShadow: `0 2px 8px ${section.accentColor}20`,
                },
              }}
            >
              {/* Section Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  mb: '10px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                    backgroundColor: section.accentColor,
                  }}
                >
                  <IconComponent size={16} color="white" strokeWidth={2.2} />
                </Box>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#1F2937',
                    lineHeight: 1.2,
                  }}
                >
                  {section.label}
                </Typography>
              </Box>

              {/* Section Content */}
              <Typography
                sx={{
                  fontSize: '13px',
                  fontWeight: 500,
                  lineHeight: 1.5,
                  color: '#4B5563',
                  letterSpacing: '-0.2px',
                }}
              >
                {section.content}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default SavedMenuSummaryCard
